import AppContext from 'App/AppContext';
import Task from 'models/tasks/task';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiGetCommentCount } from 'services/Api/commentService';
import { apiRequest } from 'services/Auth/authConfig';
import { getTaskPivotCounts, ITaskPivotCounts } from './taskPivotCounts';

// Number of messages in the task's comment trail, for the count on the Activity tab. Deliberately counts
// messages only (product decision), not other trail entries.
// A task loaded by GET tasks/{id} brings its count (task.commentCount); a task taken from a list, board or
// widget does not (undefined), so the count is then fetched once per comment trail, and ignored when the task
// changes or the dialog closes. Once the Activity tab is open, ActivityTrail reports its own count
// (onCommentCountChange) after every load, add, edit and remove; that report replaces the task's count for as
// long as it was made for this trail and this task count, so a reloaded or switched task falls back to its
// own count. Undefined (no "(0)") when empty.
export const useTaskActivityCount = (
  task: Task,
): { count: number | undefined; onCommentCountChange: (count: number) => void } => {
  const appContext = useContext(AppContext);
  const appContextRef = useRef(appContext);
  appContextRef.current = appContext;
  const { commentTrailId, commentCount } = task;
  const [reported, setReported] = useState<
    { trailId: number; taskCount: number | undefined; count: number } | undefined
  >(undefined);
  const [fetched, setFetched] = useState<{ trailId: number; count: number } | undefined>(undefined);

  useEffect(() => {
    if (commentCount !== undefined || commentTrailId <= 0) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        const accessToken = await appContextRef.current.getAccessToken(apiRequest.scopes);
        const count = await apiGetCommentCount(commentTrailId, accessToken);
        if (!cancelled) setFetched({ trailId: commentTrailId, count: count });
      } catch (err) {
        if (!cancelled) appContextRef.current.setError(err);
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, [commentTrailId, commentCount]);

  const onCommentCountChange = useCallback(
    (newCount: number) => {
      setReported({ trailId: commentTrailId, taskCount: commentCount, count: newCount });
    },
    [commentTrailId, commentCount],
  );

  const isReported = reported?.trailId === commentTrailId && reported.taskCount === commentCount;
  const taskCount = commentCount ?? (fetched?.trailId === commentTrailId ? fetched.count : 0);
  const count = isReported ? reported.count : taskCount;

  return { count: count > 0 ? count : undefined, onCommentCountChange };
};

// Counts for the Checklist, Context, Library and Activity tabs, and the callback that ActivityTrail uses to
// report its comment count. SingleTask loads the task relations (initData) before it sets the task, so
// task.taskRelations is complete here.
export const useTaskPivotCounts = (
  task: Task,
): { pivotCounts: ITaskPivotCounts; onActivityCountChange: (count: number) => void } => {
  const { count, onCommentCountChange } = useTaskActivityCount(task);
  const pivotCounts = useMemo(() => ({ ...getTaskPivotCounts(task), activity: count }), [task, count]);

  return { pivotCounts, onActivityCountChange: onCommentCountChange };
};
